import { Box, Grid } from '@mui/material';
import React from 'react';
import 'typeface-montserrat';
import { Subheading } from '../../components/shared/typography/PrimaryTypography';
import ViewsWidget, { ViewsWidgetConfig } from '../../components/home/ViewsWidget';
import readiness_gauge_img from '../../assets/home_dashboard/readiness_gauge.webp';
import carbon_dashboard_img from '../../assets/home_dashboard/carbon_dashboard.webp';
import metric_collection_img from '../../assets/home_dashboard/metric_collection.webp';
import utility_dashboards_img from '../../assets/home_dashboard/utility_dashboards.webp';
import asset_management_dashboards_img from '../../assets/home_dashboard/asset_management_dashboards.webp';
import ModuleAccessDeniedModal from '../../components/shared/modal/ModuleAccessDeniedModal';
import { PBIReport, User } from '@esg/esg-global-types';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { getPBIReports } from '../../lib/app/powerbi/pbi_report';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { UserContext } from '../../context/UserContext';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import {
  CUSTOM_DASHBOARD_VIEW_ID,
  Module,
  View,
  hasCustomDashboard,
  modules,
  resolveViewName
} from '../../lib/app/navigation';

interface GridViewsWidget {
  views: Array<ViewsWidgetConfig>;
  size: number;
}

/**
 * Home view for logged in Syntiro App
 * @returns {JSX.Element}
 */
const HomeDashboard = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user }: { user: User | null } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [pbiReports, setPBIReports] = React.useState<Array<PBIReport> | null>(null);
  const [displayAccessDeniedModal, setDisplayAccessDeniedModal] = React.useState<boolean>(false);

  const utility_management_module: Module | undefined = modules.find(
    (module: Module) => module.id === 'utility_management'
  );
  const utility_management_views: Array<View> =
    utility_management_module?.views.filter((view: View) => view.require_pbi_report) ?? [];

  const asset_management_module: Module | undefined = modules.find(
    (module: Module) => module.id === 'asset_management'
  );
  const asset_management_views: Array<View> =
    asset_management_module?.views.filter((view: View) => view.require_pbi_report) ?? [];

  // Display access denied modal when clicking on view links that that current company user can't access.
  const handleDisplayAccessDeniedModal = (): void => {
    setDisplayAccessDeniedModal(true);
  };

  // Check if the current user has access to a given view, and if it is a PowerBI view, check if the company has a configured report for it.
  const checkValidViewAccess = (
    module_id: string,
    view_id: string,
    require_pbi_report: boolean
  ): boolean => {
    const user_access: ViewAccess =
      user && group && company
        ? getViewAccess(user, group.id, company.id, `${module_id}.${view_id}`)
        : null;
    const valid_pbi: boolean =
      !require_pbi_report ||
      (!!pbiReports && !!pbiReports.find((report: PBIReport) => report.view === view_id));
    return !!user_access && user_access !== 'none' && valid_pbi;
  };

  // Widgets containing 1 or multiple view configs to display.
  const sustainability_widgets: Array<GridViewsWidget> = [
    {
      views: [
        {
          title: 'Readiness Gauge',
          description: `Evaluate your business’ sustainability score, identify weak spots and enable an actionable ESG strategy`,
          image: readiness_gauge_img,
          link: '/sustainability/readiness',
          module_id: 'sustainability',
          view_id: 'readiness_gauge',
          valid_user_access: checkValidViewAccess('sustainability', 'readiness_gauge', false)
        }
      ],
      size: 4
    },
    {
      views: [
        {
          title: 'Carbon Dashboard',
          description: `Visualize carbon emission data to track year-on-year changes, KPIs and major emission contributors`,
          image: carbon_dashboard_img,
          link: '/sustainability/carbon_dashboard',
          module_id: 'sustainability',
          view_id: 'carbon_dashboard',
          valid_user_access: checkValidViewAccess('sustainability', 'carbon_dashboard', true)
        }
      ],
      size: 4
    },
    {
      views: [
        {
          title: 'Metric Collection',
          description: `Select ESG standards to track metrics, ensure compliance and monitor Scope 3 carbon emissions across suppliers`,
          image: metric_collection_img,
          link: '/sustainability/metrics',
          module_id: 'sustainability',
          view_id: 'metric_collection',
          valid_user_access: checkValidViewAccess('sustainability', 'metric_collection', false),
          align_left: true,
          image_bottom: true
        }
      ],
      size: 3
    }
  ];

  // Map views from database to widget data and sort by valid user access first.
  // Show all PBI utility views; keep the custom dashboard hidden only when the
  // company has no custom dashboard configured.
  const utilityManagementWidgetViews: Array<ViewsWidgetConfig> = utility_management_views
    .filter((view: View) => view.id !== CUSTOM_DASHBOARD_VIEW_ID || hasCustomDashboard(company?.id))
    .map((view: View) => ({
      title: resolveViewName(view, company?.id),
      description: view.description,
      image: utility_dashboards_img,
      link: `/utility_management/${view.id}`,
      module_id: 'utility_management',
      view_id: view.id,
      valid_user_access: checkValidViewAccess('utility_management', view.id, true),
      image_bottom: true
    }))
    .sort(
      (a: ViewsWidgetConfig, b: ViewsWidgetConfig) =>
        Number(b.valid_user_access) - Number(a.valid_user_access)
    );

  const utilityManagementWidget: GridViewsWidget = {
    views: utilityManagementWidgetViews,
    size: 5.5
  };

  // Map views from database to widget data and sort by valid user access first.
  const assetManagementWidgetViews: Array<ViewsWidgetConfig> = asset_management_views
    .map((view: View) => ({
      title: resolveViewName(view, company?.id),
      description: view.description,
      image: asset_management_dashboards_img,
      link: `/asset_management/${view.id}`,
      module_id: 'asset_management',
      view_id: view.id,
      valid_user_access: checkValidViewAccess('asset_management', view.id, true),
      image_bottom: true
    }))
    .sort(
      (a: ViewsWidgetConfig, b: ViewsWidgetConfig) =>
        Number(b.valid_user_access) - Number(a.valid_user_access)
    );

  const asset_management_widget: GridViewsWidget = {
    views: assetManagementWidgetViews,
    size: 5.5
  };

  React.useEffect(() => {
    try {
      (async () => {
        if (group && company) {
          const pbi_reports: Array<PBIReport> = await getPBIReports(group.id, company.id);
          setPBIReports(pbi_reports);
        }
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: HomeDashboard failed on an unknown error while initialising.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [company]);

  return (
    <>
      {/* Access Denied for view modal. */}
      <ModuleAccessDeniedModal
        open={displayAccessDeniedModal}
        title="Access Denied"
        description={`You do not have access to this module. Go back to choose another module, 
      or contact Energy Partners to find out more about expanding your service offering.`}
        handleCloseModal={() => setDisplayAccessDeniedModal(false)}
      />
      <Box sx={{ minHeight: '80vh' }}>
        <Subheading weight="Bold" sx={{ mb: 2 }}>
          SUSTAINABILITY
        </Subheading>
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {sustainability_widgets.map((views_widget: GridViewsWidget, i) => (
            <Grid size={views_widget.size} key={`sustainability-views-${i}`}>
              <ViewsWidget
                views={views_widget.views}
                pbi_reports={pbiReports}
                handleDisplayAccessDeniedModal={handleDisplayAccessDeniedModal}
              />
            </Grid>
          ))}
          <Grid
            size={utilityManagementWidget.size}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <Subheading weight="Bold" sx={{ mb: 2 }}>
              UTILITY MANAGEMENT
            </Subheading>
            <ViewsWidget
              views={utilityManagementWidget.views}
              pbi_reports={pbiReports}
              handleDisplayAccessDeniedModal={handleDisplayAccessDeniedModal}
            />
          </Grid>
          <Grid
            size={asset_management_widget.size}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <Subheading weight="Bold" sx={{ mb: 2 }}>
              ASSET MANAGEMENT
            </Subheading>
            <ViewsWidget
              views={asset_management_widget.views}
              pbi_reports={pbiReports}
              handleDisplayAccessDeniedModal={handleDisplayAccessDeniedModal}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default HomeDashboard;
